import { Check, ChevronDown } from "lucide-react";
import { useId, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { useShowValidationErrors } from "~/common/hooks";
import type { MutationRefetchQueries } from "~/common/types";
import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  SearchableMultiSelect,
  Textarea,
} from "~/components/ui";
import { PropertySchemaAssociationsList, usePropertySchemaAssociationsForm } from "~/features/admin/propertySchemas";
import { useDataImportProjectsModel } from "~/features/dataImportProjects/context";
import { useBootstrapDataImportProjectForm } from "~/features/dataImportProjects/hooks/useBootstrapDataImportProjectForm";
import { usePropertySchemaCsvUpload } from "~/features/dataImportProjects/hooks/usePropertySchemaCsvUpload";
import { type BootstrapFormData, zBootstrapFormDataKeys } from "~/features/dataImportProjects/types";
import { cn } from "~/lib/utils";

export const BOOTSTRAP_DATA_IMPORT_PROJECT_FORM_ID = "bootstrap-data-import-project-form";

interface TemplateOption {
  id: string;
  name: string;
}

/** Single-select with a typeahead, mirroring what the MUI Autocomplete offered here. */
function TemplateSelect({
  options,
  value,
  onChange,
  placeholder,
  searchPlaceholder,
  emptyText,
  id,
  ref,
  "aria-describedby": ariaDescribedBy,
  "aria-invalid": ariaInvalid,
}: {
  options: TemplateOption[];
  value: TemplateOption | null;
  onChange: (next: TemplateOption | null) => void;
  placeholder: string;
  searchPlaceholder: string;
  emptyText: string;
  id?: string;
  // Forwarded so FormControl can wire the error message and useShowValidationErrors can focus here.
  ref?: React.Ref<HTMLButtonElement>;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean;
}) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          ref={ref}
          type="button"
          variant="outline"
          aria-describedby={ariaDescribedBy}
          aria-invalid={ariaInvalid}
          className="w-full justify-between font-normal"
        >
          <span className={cn("truncate", !value && "text-fg-3")}>{value ? value.name : placeholder}</span>
          <ChevronDown className="shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-(--radix-popover-trigger-width) p-0" align="start">
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.id}
                  // cmdk keys items by `value`; names are not unique so the id has to be it.
                  value={option.id}
                  keywords={[option.name]}
                  onSelect={() => {
                    onChange(value?.id === option.id ? null : option);
                    setOpen(false);
                  }}
                >
                  <Check className={cn("mr-2 size-4", value?.id === option.id ? "opacity-100" : "opacity-0")} />
                  <span className="truncate">{option.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

interface BootstrapDataImportProjectFormProps {
  onSubmit: () => void;
  refetchQueriesOnBootstrap: MutationRefetchQueries;
}

export function BootstrapDataImportProjectForm({
  onSubmit,
  refetchQueriesOnBootstrap,
}: BootstrapDataImportProjectFormProps) {
  const { t } = useTranslation();
  const { users, references, dataSourceTemplates } = useDataImportProjectsModel();
  const csvInputRef = useRef<HTMLInputElement>(null);
  const csvInputId = useId();

  const methods = useForm<BootstrapFormData>({
    defaultValues: {
      year: new Date().getFullYear().toString(),
      users: [],
      references: [],
      documentUrl: "",
      description: "",
      dataSourceTemplate: null,
    },
  });
  const { reset, handleSubmit, control, setError } = methods;
  const showValidationErrors = useShowValidationErrors(setError, zBootstrapFormDataKeys);
  const propertySchemaAssociations = usePropertySchemaAssociationsForm(control);

  const { handlePropertySchemaCsvUpload } = usePropertySchemaCsvUpload({
    propertySchemaAssociations,
    showSnackbar: (message, severity) => (severity === "error" ? toast.error(message) : toast.success(message)),
    t,
  });

  const { submit } = useBootstrapDataImportProjectForm({
    refetchQueriesOnBootstrap,
    onSuccess: () => {
      reset();
      onSubmit();
    },
    onValidationError: (error) => {
      showValidationErrors(error.fields);
    },
  });

  const userOptions = users.map((user) => ({ id: user.id, label: user.email }));
  const referenceOptions = references.map((reference) => ({ id: reference.id, label: reference.title }));
  const csvUploadLabel = t("feature.dataImportProjects.form.uploadPropertySchema");

  return (
    <Form {...methods}>
      <form id={BOOTSTRAP_DATA_IMPORT_PROJECT_FORM_ID} onSubmit={handleSubmit(submit)} className="space-y-6">
        <FormField
          control={control}
          name="year"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("model.dataImportProject.year")}</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  value={field.value ?? ""}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  ref={field.ref}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="name"
          rules={{ required: t("ui.required") }}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("model.common.name")}</FormLabel>
              <FormControl>
                <Input
                  value={field.value ?? ""}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  ref={field.ref}
                  required
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="users"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("model.user.modelNames")}</FormLabel>
              <FormControl>
                <SearchableMultiSelect
                  placeholder={t("model.user.modelNames")}
                  searchPlaceholder={t("ui.search")}
                  emptyText={t("feature.common.noData")}
                  options={userOptions}
                  selectedIds={(field.value ?? []).map((user: { id: string }) => user.id)}
                  onChange={(ids) =>
                    field.onChange(
                      ids.map((id) => {
                        const user = users.find((candidate) => candidate.id === id);
                        return { id, email: user?.email ?? id };
                      }),
                    )
                  }
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="references"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("model.reference.modelNames")}</FormLabel>
              <FormControl>
                <SearchableMultiSelect
                  placeholder={t("model.reference.modelNames")}
                  searchPlaceholder={t("ui.search")}
                  emptyText={t("feature.common.noData")}
                  options={referenceOptions}
                  selectedIds={(field.value ?? []).map((reference: { id: string }) => reference.id)}
                  onChange={(ids) =>
                    field.onChange(
                      ids.map((id) => ({
                        id,
                        title: references.find((candidate) => candidate.id === id)?.title ?? id,
                      })),
                    )
                  }
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="documentUrl"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("model.dataImportProject.document")}</FormLabel>
              <FormControl>
                <Input value={field.value ?? ""} onChange={field.onChange} onBlur={field.onBlur} ref={field.ref} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="description"
          rules={{ required: t("ui.required") }}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("model.dataImportProject.description")}</FormLabel>
              <FormControl>
                <Textarea rows={4} value={field.value ?? ""} onChange={field.onChange} onBlur={field.onBlur} required />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="dataSourceTemplate"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("model.dataSourceTemplate.modelName")}</FormLabel>
              <FormControl>
                <TemplateSelect
                  ref={field.ref}
                  options={dataSourceTemplates}
                  value={field.value}
                  onChange={field.onChange}
                  placeholder={t("model.dataSourceTemplate.modelName")}
                  searchPlaceholder={t("ui.search")}
                  emptyText={t("feature.common.noData")}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="space-y-2">
          <PropertySchemaAssociationsList
            title={<h3 className="text-14-bold text-fg-1">{t("model.propertySchema.modelNames")}</h3>}
            fields={propertySchemaAssociations.fields}
            onRemove={propertySchemaAssociations.remove}
            onMove={propertySchemaAssociations.move}
          />
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={propertySchemaAssociations.append}>
              {t("feature.dataImportProjects.form.addPropertySchema")}
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={propertySchemaAssociations.fields.length > 0}
              onClick={() => csvInputRef.current?.click()}
            >
              {csvUploadLabel}
            </Button>
            <Label htmlFor={csvInputId} className="sr-only">
              {csvUploadLabel}
            </Label>
            <input
              id={csvInputId}
              ref={csvInputRef}
              type="file"
              className="sr-only"
              tabIndex={-1}
              accept=".csv,text/csv,application/vnd.ms-excel"
              onChange={handlePropertySchemaCsvUpload}
            />
          </div>
        </div>
      </form>
    </Form>
  );
}
